import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Navigate, useNavigate } from "react-router-dom";
import { Logo } from "../components/Logo";
import { api, API, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";
import { LogOut, CalendarCheck2, Mail, Loader2, BadgeCheck, XCircle, Clock, MailOpen, Download, Search, FileText, ShieldCheck, Paperclip, Activity, Sparkles, FileSignature, PenTool } from "lucide-react";
import { format } from "date-fns";
import DatePicker from "../components/DatePicker";

function StatBox({ label, value, accent = "slate" }) {
 const colors = {
 slate: "text-sage-800 bg-sage-50",
 gold: "text-gold-700 bg-gold-50",
 sage: "text-sage-700 bg-sage-50",
 ink: "text-ink bg-cream",
 };
 return (
 <div className="card-soft p-6">
 <p className="text-xs uppercase tracking-[0.2em] text-muted_text font-semibold">{label}</p>
 <div className={`mt-3 inline-flex items-baseline gap-2 px-3 py-1.5 rounded-xl ${colors[accent]}`}>
 <span className="font-heading text-3xl font-semibold leading-none">{value}</span>
 </div>
 </div>
 );
}

function StatusPill({ s }) {
 const map = {
 pending: "bg-gold-50 text-gold-700 border-gold-200",
 confirmed: "bg-sage-50 text-sage-700 border-sage-200",
 cancelled: "bg-red-50 text-red-700 border-red-200",
 verified: "bg-sage-50 text-sage-700 border-sage-200",
 declined: "bg-red-50 text-red-700 border-red-200",
 };
 return <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border ${map[s] || "bg-cream text-ink border-[#E2E8F0]"}`}>{s}</span>;
}

async function downloadFile(fileId, name) {
 try {
 const res = await api.get(`/admin/files/${fileId}`, { responseType: "blob" });
 const url = window.URL.createObjectURL(new Blob([res.data]));
 const a = document.createElement("a");
 a.href = url;
 a.download = name || "file";
 a.click();
 window.URL.revokeObjectURL(url);
 } catch (e) {
 toast.error("Could not download file");
 }
}

function DocumentsList({ docs }) {
 if (!docs || docs.length === 0) return <span className="text-xs text-muted_text">, </span>;
 return (
 <div className="flex flex-wrap gap-1.5">
 {docs.map((d) => (
 <button
 key={d.id}
 data-testid={`doc-${d.id}`}
 onClick={() => downloadFile(d.id, d.original_name)}
 className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-sage-50 text-sage-800 hover:bg-sage-100"
 title={`${d.kind}: ${d.original_name}`}
 >
 <Paperclip className="w-3 h-3" /> {d.kind}
 </button>
 ))}
 </div>
 );
}

function exportCSV(path) {
 const token = localStorage.getItem("gls_token");
 fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } })
 .then((r) => r.blob())
 .then((blob) => {
 const url = window.URL.createObjectURL(blob);
 const a = document.createElement("a");
 a.href = url;
 a.download = path.split("/").pop();
 a.click();
 window.URL.revokeObjectURL(url);
 })
 .catch(() => toast.error("Export failed"));
}

export default function AdminDashboard() {
 const { user, logout } = useAuth();
 const nav = useNavigate();
 const [stats, setStats] = useState(null);
 const [appts, setAppts] = useState([]);
 const [contacts, setContacts] = useState([]);
 const [insurance, setInsurance] = useState([]);
 const [intake, setIntake] = useState([]);
 const [funnel, setFunnel] = useState(null);
 const [consents, setConsents] = useState([]);
 const [loading, setLoading] = useState(true);

 // filters
 const [statusFilter, setStatusFilter] = useState("");
 const [dateFrom, setDateFrom] = useState("");
 const [dateTo, setDateTo] = useState("");
 const [q, setQ] = useState("");

 const apptsQuery = useMemo(() => {
 const params = new URLSearchParams();
 if (statusFilter) params.set("status", statusFilter);
 if (dateFrom) params.set("date_from", dateFrom);
 if (dateTo) params.set("date_to", dateTo);
 if (q) params.set("q", q);
 const s = params.toString();
 return s ? `?${s}` : "";
 }, [statusFilter, dateFrom, dateTo, q]);

 async function refresh() {
 setLoading(true);
 try {
 const [s, a, c, ins, intk, f, cn] = await Promise.all([
 api.get("/admin/stats"),
 api.get(`/admin/appointments${apptsQuery}`),
 api.get("/admin/contacts"),
 api.get("/admin/insurance-verifications"),
 api.get("/admin/intake-forms"),
 api.get("/admin/care-match/funnel"),
 api.get("/admin/consents"),
 ]);
 setStats(s.data); setAppts(a.data); setContacts(c.data); setInsurance(ins.data); setIntake(intk.data); setFunnel(f.data); setConsents(cn.data);
 } catch (e) {
 toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
 } finally { setLoading(false); }
 }

 useEffect(() => { if (user) refresh(); }, [user, apptsQuery]);

 if (user === undefined) return null;
 if (!user) return <Navigate to="/admin/login" replace />;

 async function setStatus(id, status) {
 try { await api.patch(`/admin/appointments/${id}`, { status }); toast.success(`Marked as ${status}`); refresh(); }
 catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
 }
 async function setInsuranceStatus(id, status) {
 try { await api.patch(`/admin/insurance-verifications/${id}`, { status }); toast.success(`Marked as ${status}`); refresh(); }
 catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
 }
 async function setIntakeStatus(id, status) {
 try { await api.patch(`/admin/intake-forms/${id}`, { status }); toast.success(`Marked as ${status.replace("_", " ")}`); refresh(); }
 catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
 }
 async function markRead(id) {
 try { await api.patch(`/admin/contacts/${id}/read`); refresh(); }
 catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
 }
 function doLogout() { logout(); nav("/admin/login", { replace: true }); }
 function clearFilters() { setStatusFilter(""); setDateFrom(""); setDateTo(""); setQ(""); }

 return (
 <div className="min-h-screen bg-cream">
 <Toaster richColors position="top-right" />
 <header data-testid="admin-header" className="sticky top-0 z-30 glass border-b border-[#E8EBE4]">
 <div className="container-pad max-w-7xl mx-auto h-[72px] flex items-center justify-between">
 <Logo />
 <div className="flex items-center gap-4">
 <span className="hidden sm:inline text-sm text-muted_text" data-testid="admin-user">{user.email}</span>
 <Button onClick={doLogout} data-testid="admin-logout" className="!h-10 !rounded-full" variant="outline">
 <LogOut className="w-4 h-4 mr-1.5" strokeWidth={1.75} /> Sign out
 </Button>
 </div>
 </div>
 </header>

 <div className="container-pad max-w-7xl mx-auto py-12">
 <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
 <div>
 <p className="eyebrow"><span className="w-6 h-px bg-gold-500" /> Admin dashboard</p>
 <h1 className="mt-3 font-heading text-3xl sm:text-4xl tracking-tight text-ink">Care operations</h1>
 </div>
 <Button onClick={refresh} disabled={loading} className="!rounded-full !h-10" variant="outline" data-testid="admin-refresh">
 {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh"}
 </Button>
 </div>

 <div data-testid="admin-stats" className="grid sm:grid-cols-2 lg:grid-cols-6 gap-5 mb-10">
 <StatBox label="Total appointments" value={stats?.total_appointments ?? ", "} accent="slate" />
 <StatBox label="Pending" value={stats?.pending ?? ", "} accent="gold" />
 <StatBox label="Confirmed" value={stats?.confirmed ?? ", "} accent="sage" />
 <StatBox label="Unread messages" value={stats?.unread_messages ?? ", "} accent="ink" />
 <StatBox label="Insurance pending" value={stats?.insurance_pending ?? ", "} accent="gold" />
 <StatBox label="Intakes submitted" value={stats?.intake_submitted ?? ", "} accent="sage" />
 </div>

 <Tabs defaultValue="appts">
 <TabsList className="bg-paper border border-[#E8EBE4] rounded-full p-1 h-12">
 <TabsTrigger value="appts" data-testid="tab-appts" className="rounded-full px-6 h-10 data-[state=active]:bg-sage-700 data-[state=active]:text-white">
 <CalendarCheck2 className="w-4 h-4 mr-2" strokeWidth={1.75} /> Appointments
 </TabsTrigger>
 <TabsTrigger value="ins" data-testid="tab-ins" className="rounded-full px-6 h-10 data-[state=active]:bg-sage-700 data-[state=active]:text-white">
 <ShieldCheck className="w-4 h-4 mr-2" strokeWidth={1.75} /> Insurance
 </TabsTrigger>
 <TabsTrigger value="intake" data-testid="tab-intake" className="rounded-full px-6 h-10 data-[state=active]:bg-sage-700 data-[state=active]:text-white">
 <FileSignature className="w-4 h-4 mr-2" strokeWidth={1.75} /> Intake forms
 </TabsTrigger>
 <TabsTrigger value="consents" data-testid="tab-consents" className="rounded-full px-6 h-10 data-[state=active]:bg-sage-700 data-[state=active]:text-white">
 <BadgeCheck className="w-4 h-4 mr-2" strokeWidth={1.75} /> Consents
 </TabsTrigger>
 <TabsTrigger value="msgs" data-testid="tab-msgs" className="rounded-full px-6 h-10 data-[state=active]:bg-sage-700 data-[state=active]:text-white">
 <Mail className="w-4 h-4 mr-2" strokeWidth={1.75} /> Messages
 </TabsTrigger>
 <TabsTrigger value="funnel" data-testid="tab-funnel" className="rounded-full px-6 h-10 data-[state=active]:bg-sage-700 data-[state=active]:text-white">
 <Activity className="w-4 h-4 mr-2" strokeWidth={1.75} /> Care Match Funnel
 </TabsTrigger>
 </TabsList>

 <TabsContent value="appts" className="mt-6 space-y-5">
 {/* Filter bar */}
 <div data-testid="admin-filters" className="card-soft p-4 sm:p-5 grid sm:grid-cols-2 lg:grid-cols-12 gap-3 items-end">
 <div className="lg:col-span-4">
 <label className="text-xs uppercase tracking-[0.16em] font-semibold text-muted_text">Search</label>
 <div className="relative mt-2">
 <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted_text" />
 <Input data-testid="filter-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, email, or phone..." className="h-11 pl-9" />
 </div>
 </div>
 <div className="lg:col-span-2">
 <label className="text-xs uppercase tracking-[0.16em] font-semibold text-muted_text">Status</label>
 <select data-testid="filter-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="mt-2 h-11 w-full rounded-md border border-input bg-background px-3 text-sm">
 <option value="">All</option>
 <option value="pending">Pending</option>
 <option value="confirmed">Confirmed</option>
 <option value="cancelled">Cancelled</option>
 </select>
 </div>
 <div className="lg:col-span-2">
 <label className="text-xs uppercase tracking-[0.16em] font-semibold text-muted_text">From</label>
 <div className="mt-2">
 <DatePicker testId="filter-from" value={dateFrom} onChange={setDateFrom} placeholder="Start date" />
 </div>
 </div>
 <div className="lg:col-span-2">
 <label className="text-xs uppercase tracking-[0.16em] font-semibold text-muted_text">To</label>
 <div className="mt-2">
 <DatePicker testId="filter-to" value={dateTo} onChange={setDateTo} placeholder="End date" />
 </div>
 </div>
 <div className="lg:col-span-2 flex gap-2">
 <Button type="button" variant="outline" onClick={clearFilters} data-testid="filter-clear" className="!h-11 !rounded-full flex-1">Clear</Button>
 <Button type="button" onClick={() => exportCSV("/admin/appointments.csv")} data-testid="export-appts-csv" className="btn-gold !h-11 !rounded-full !text-sm whitespace-nowrap">
 <Download className="w-4 h-4" strokeWidth={1.75} /> CSV
 </Button>
 </div>
 </div>

 <div className="card-soft overflow-hidden">
 <div className="overflow-x-auto">
 <table data-testid="appts-table" className="w-full text-sm">
 <thead className="bg-cream text-muted_text text-xs uppercase tracking-[0.16em]">
 <tr>
 <th className="text-left px-5 py-4 font-semibold">Patient</th>
 <th className="text-left px-5 py-4 font-semibold">Service</th>
 <th className="text-left px-5 py-4 font-semibold">When</th>
 <th className="text-left px-5 py-4 font-semibold">Insurance</th>
 <th className="text-left px-5 py-4 font-semibold">Docs</th>
 <th className="text-left px-5 py-4 font-semibold">Consent</th>
 <th className="text-left px-5 py-4 font-semibold">Status</th>
 <th className="text-right px-5 py-4 font-semibold">Actions</th>
 </tr>
 </thead>
 <tbody>
 {appts.length === 0 && (
 <tr><td colSpan={8} className="px-5 py-12 text-center text-muted_text" data-testid="appts-empty">No appointments match the current filters.</td></tr>
 )}
 {appts.map((a) => (
 <tr key={a.id} data-testid={`appt-row-${a.id}`} className="border-t border-[#E8EBE4] align-top">
 <td className="px-5 py-4">
 <p className="font-medium text-ink">{a.full_name}</p>
 <p className="text-xs text-muted_text">{a.email}</p>
 <p className="text-xs text-muted_text">{a.phone}</p>
 </td>
 <td className="px-5 py-4">{a.service}</td>
 <td className="px-5 py-4">
 <p>{format(new Date(a.date + "T" + a.time), "EEE, MMM d")}</p>
 <p className="text-xs text-muted_text">{a.time}</p>
 </td>
 <td className="px-5 py-4">
 {a.insurance && (a.insurance.provider || a.insurance.member_id) ? (
 <div className="text-xs">
 <p className="font-medium text-ink">{a.insurance.provider || ", "}</p>
 {a.insurance.member_id && <p className="text-muted_text">ID: {a.insurance.member_id}</p>}
 {a.insurance.group_number && <p className="text-muted_text">Grp: {a.insurance.group_number}</p>}
 </div>
 ) : <span className="text-xs text-muted_text">, </span>}
 </td>
 <td className="px-5 py-4"><DocumentsList docs={a.documents} /></td>
 <td className="px-5 py-4">
 {a.consent_signed_at ? (
 <span data-testid={`consent-status-${a.id}`} className="inline-flex items-center gap-1 text-xs px-2.5 h-6 rounded-full bg-sage-500/15 text-sage-700 whitespace-nowrap">
 <BadgeCheck className="w-3.5 h-3.5" /> Signed {format(new Date(a.consent_signed_at), "MMM d")}
 </span>
 ) : (
 <span data-testid={`consent-status-${a.id}`} className="inline-flex items-center gap-1 text-xs px-2.5 h-6 rounded-full bg-amber-500/15 text-amber-700 whitespace-nowrap">
 <Clock className="w-3.5 h-3.5" /> Awaiting
 </span>
 )}
 </td>
 <td className="px-5 py-4"><StatusPill s={a.status} /></td>
 <td className="px-5 py-4">
 <div className="flex justify-end gap-2 flex-wrap">
 {a.status !== "confirmed" && (
 <button data-testid={`appt-confirm-${a.id}`} onClick={() => setStatus(a.id, "confirmed")} className="text-xs px-3 h-8 rounded-full bg-sage-500/15 text-sage-700 hover:bg-sage-500/25 inline-flex items-center gap-1">
 <BadgeCheck className="w-3.5 h-3.5" /> Confirm
 </button>
 )}
 {a.status !== "pending" && (
 <button data-testid={`appt-pending-${a.id}`} onClick={() => setStatus(a.id, "pending")} className="text-xs px-3 h-8 rounded-full bg-gold-500/15 text-gold-700 hover:bg-gold-500/25 inline-flex items-center gap-1">
 <Clock className="w-3.5 h-3.5" /> Pending
 </button>
 )}
 {a.status !== "cancelled" && (
 <button data-testid={`appt-cancel-${a.id}`} onClick={() => setStatus(a.id, "cancelled")} className="text-xs px-3 h-8 rounded-full bg-red-500/10 text-red-700 hover:bg-red-500/20 inline-flex items-center gap-1">
 <XCircle className="w-3.5 h-3.5" /> Cancel
 </button>
 )}
 </div>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 </div>
 </TabsContent>

 <TabsContent value="ins" className="mt-6">
 <div className="grid gap-4">
 {insurance.length === 0 && (
 <div className="card-soft p-10 text-center text-muted_text" data-testid="ins-empty">No insurance verification requests yet.</div>
 )}
 {insurance.map((m) => (
 <article key={m.id} data-testid={`ins-${m.id}`} className="card-soft p-6">
 <div className="flex flex-wrap items-start justify-between gap-3">
 <div>
 <p className="font-heading font-semibold text-ink">{m.full_name} <span className="text-muted_text font-normal text-sm">· {m.email} · {m.phone}</span></p>
 <div className="mt-2 grid sm:grid-cols-3 gap-x-6 gap-y-1 text-sm">
 <p><span className="text-muted_text">Provider: </span><span className="font-medium">{m.provider}</span></p>
 <p><span className="text-muted_text">Member ID: </span><span className="font-medium">{m.member_id}</span></p>
 {m.group_number && <p><span className="text-muted_text">Group: </span><span className="font-medium">{m.group_number}</span></p>}
 {m.dob && <p><span className="text-muted_text">DOB: </span>{m.dob}</p>}
 </div>
 {m.notes && <p className="mt-3 text-sm text-ink/80">{m.notes}</p>}
 <div className="mt-3"><DocumentsList docs={m.documents} /></div>
 </div>
 <div className="flex flex-col items-end gap-2">
 <span className="text-xs text-muted_text">{format(new Date(m.created_at), "MMM d, h:mm a")}</span>
 <StatusPill s={m.status} />
 <div className="flex gap-2">
 {m.status !== "verified" && (
 <button data-testid={`ins-verify-${m.id}`} onClick={() => setInsuranceStatus(m.id, "verified")} className="text-xs px-3 h-8 rounded-full bg-sage-500/15 text-sage-700 hover:bg-sage-500/25 inline-flex items-center gap-1">
 <BadgeCheck className="w-3.5 h-3.5" /> Verify
 </button>
 )}
 {m.status !== "declined" && (
 <button data-testid={`ins-decline-${m.id}`} onClick={() => setInsuranceStatus(m.id, "declined")} className="text-xs px-3 h-8 rounded-full bg-red-500/10 text-red-700 hover:bg-red-500/20 inline-flex items-center gap-1">
 <XCircle className="w-3.5 h-3.5" /> Decline
 </button>
 )}
 </div>
 </div>
 </div>
 </article>
 ))}
 </div>
 </TabsContent>

 <TabsContent value="intake" className="mt-6">
 <IntakeAdminList items={intake} onStatus={setIntakeStatus} />
 </TabsContent>

 <TabsContent value="msgs" className="mt-6 space-y-4">
 <div className="flex justify-end">
 <Button onClick={() => exportCSV("/admin/contacts.csv")} data-testid="export-msgs-csv" className="btn-gold !h-10 !rounded-full !text-sm">
 <Download className="w-4 h-4" strokeWidth={1.75} /> Export messages CSV
 </Button>
 </div>
 <div className="grid gap-4">
 {contacts.length === 0 && (
 <div className="card-soft p-10 text-center text-muted_text" data-testid="msgs-empty">No messages yet.</div>
 )}
 {contacts.map((m) => (
 <article key={m.id} data-testid={`msg-${m.id}`} className={`card-soft p-6 ${m.is_read ? "" : "ring-1 ring-gold-300/60"}`}>
 <div className="flex flex-wrap items-start justify-between gap-3">
 <div>
 <p className="font-heading font-semibold text-ink">{m.full_name} <span className="text-muted_text font-normal text-sm">· {m.email}</span></p>
 {m.phone && <p className="text-xs text-muted_text">{m.phone}</p>}
 {m.subject && <p className="mt-1 text-sm font-medium text-sage-800">{m.subject}</p>}
 </div>
 <div className="flex items-center gap-3">
 <span className="text-xs text-muted_text">{format(new Date(m.created_at), "MMM d, h:mm a")}</span>
 {!m.is_read ? (
 <button onClick={() => markRead(m.id)} data-testid={`msg-read-${m.id}`} className="text-xs px-3 h-8 rounded-full bg-sage-50 text-sage-800 hover:bg-sage-100 inline-flex items-center gap-1">
 <MailOpen className="w-3.5 h-3.5" /> Mark read
 </button>
 ) : (
 <span className="text-xs px-3 h-8 rounded-full bg-sage-500/15 text-sage-700 inline-flex items-center">Read</span>
 )}
 </div>
 </div>
 <p className="mt-4 text-sm text-ink whitespace-pre-line leading-relaxed">{m.message}</p>
 </article>
 ))}
 </div>
 </TabsContent>

 <TabsContent value="funnel" className="mt-6">
 <CareMatchFunnel data={funnel} loading={loading} />
 </TabsContent>

 <TabsContent value="consents" className="mt-6">
 <ConsentsTable rows={consents} loading={loading} />
 </TabsContent>
 </Tabs>
 </div>
 </div>
 );
}

function FunnelStep({ label, value, helper, tone = "slate" }) {
 const tones = {
 slate: "bg-sage-50 text-sage-800",
 gold: "bg-gold-50 text-gold-700",
 sage: "bg-sage-50 text-sage-700",
 ink: "bg-cream text-ink",
 };
 return (
 <div className="card-soft p-6" data-testid={`funnel-${label.replace(/\s+/g, "-").toLowerCase()}`}>
 <p className="text-xs uppercase tracking-[0.2em] text-muted_text font-semibold">{label}</p>
 <div className={`mt-3 inline-flex items-baseline gap-2 px-3 py-1.5 rounded-xl ${tones[tone]}`}>
 <span className="font-heading text-3xl font-semibold leading-none">{value}</span>
 </div>
 {helper != null && <p className="mt-3 text-xs text-muted_text">{helper}</p>}
 </div>
 );
}

const CONCERN_LABEL = { anxiety: "Anxiety / panic", depression: "Depression / mood", adhd: "ADHD / focus", trauma: "Trauma / PTSD", sleep: "Sleep concerns", meds: "Medication management", substance: "Substance use", unsure: "Not sure yet" };
const AGE_LABEL = { under18: "Under 18", "18_25": "18-25", "26_40": "26-40", "41_65": "41-65", "65plus": "65+" };
const TELE_LABEL = { yes: "Yes", open: "Open to it", more_info: "Wants more info" };
const URGENCY_LABEL = { this_week: "This week", two_weeks: "Within 2 weeks", no_rush: "No rush" };
const STEP_LABELS = { concern: CONCERN_LABEL, age: AGE_LABEL, telehealth: TELE_LABEL, urgency: URGENCY_LABEL };
const STEP_TITLES = { concern: "Top concerns", age: "Age ranges", telehealth: "Telehealth comfort", urgency: "Urgency" };

function IntakeAdminList({ items, onStatus }) {
 if (!items || items.length === 0) {
 return <div className="card-soft p-10 text-center text-muted_text" data-testid="intake-empty">No intake forms submitted yet.</div>;
 }
 const fmt = (s) => (s ? s.length > 220 ? s.slice(0, 220) + "..." : s : ", ");
 return (
 <div className="grid gap-4">
 {items.map((f) => (
 <article key={f.id} data-testid={`intake-${f.id}`} className={`card-soft p-6 ${f.is_read ? "" : "ring-1 ring-gold-300/60"}`}>
 <div className="flex flex-wrap items-start justify-between gap-3">
 <div className="min-w-0">
 <p className="font-heading font-semibold text-ink">{f.full_name}{f.preferred_name && <span className="font-normal text-muted_text"> · "{f.preferred_name}"</span>}</p>
 <p className="text-xs text-muted_text">{f.email} · {f.phone}{f.dob && ` · DOB ${f.dob}`}</p>
 {f.appointment_id && <p className="mt-1 text-xs text-sage-800">Linked to appointment {f.appointment_id.slice(0, 8)}</p>}
 </div>
 <div className="flex flex-col items-end gap-2">
 <span className="text-xs text-muted_text">{format(new Date(f.created_at), "MMM d, h:mm a")}</span>
 <StatusPill s={f.status} />
 <div className="flex flex-wrap gap-2 justify-end">
 {f.status !== "reviewed" && (
 <button data-testid={`intake-reviewed-${f.id}`} onClick={() => onStatus(f.id, "reviewed")} className="text-xs px-3 h-8 rounded-full bg-sage-500/15 text-sage-700 hover:bg-sage-500/25 inline-flex items-center gap-1">
 <BadgeCheck className="w-3.5 h-3.5" /> Reviewed
 </button>
 )}
 {f.status !== "needs_followup" && (
 <button data-testid={`intake-followup-${f.id}`} onClick={() => onStatus(f.id, "needs_followup")} className="text-xs px-3 h-8 rounded-full bg-gold-500/15 text-gold-700 hover:bg-gold-500/25 inline-flex items-center gap-1">
 <Clock className="w-3.5 h-3.5" /> Needs follow-up
 </button>
 )}
 </div>
 </div>
 </div>

 <div className="mt-4 grid lg:grid-cols-3 gap-4 text-sm">
 <div>
 <p className="text-xs uppercase tracking-[0.18em] text-muted_text font-semibold">Chief complaint</p>
 <p className="mt-1 text-ink">{fmt(f.chief_complaint)}</p>
 </div>
 <div>
 <p className="text-xs uppercase tracking-[0.18em] text-muted_text font-semibold">Current medications</p>
 <p className="mt-1 text-ink">{fmt(f.current_medications)}</p>
 </div>
 <div>
 <p className="text-xs uppercase tracking-[0.18em] text-muted_text font-semibold">Safety notes</p>
 <p className="mt-1 text-ink">{fmt([f.suicidal_ideation_history, f.homicidal_ideation_history, f.self_harm_history].filter(Boolean).join(" · "))}</p>
 </div>
 </div>

 <div className="mt-4 flex flex-wrap gap-2 text-xs">
 {[
 ["Treatment", f.consent_to_treat],
 ["Telehealth", f.consent_telehealth],
 ["HIPAA", f.consent_hipaa_ack],
 ["ROI", f.consent_release_of_info],
 ["Financial", f.consent_financial_responsibility],
 ].map(([label, ok]) => (
 <span key={label} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border ${ok ? "bg-sage-50 text-sage-700 border-sage-200" : "bg-cream text-muted_text border-[#E2E8F0]"}`}>
 {ok ? <BadgeCheck className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} {label}
 </span>
 ))}
 <span className="ml-auto text-muted_text italic">Signed by <span className="text-ink not-italic">{f.signature_typed}</span> on {f.signature_date}</span>
 </div>
 </article>
 ))}
 </div>
 );
}

function StepBreakdown({ stepKey, items, totalSessions }) {
 const totalForStep = items.reduce((acc, i) => acc + i.count, 0);
 const labelMap = STEP_LABELS[stepKey] || {};
 return (
 <div data-testid={`funnel-step-${stepKey}`} className="card-soft p-6">
 <p className="font-heading text-base font-semibold text-ink">{STEP_TITLES[stepKey]}</p>
 <p className="text-xs text-muted_text mt-1">{totalForStep} answers across {totalSessions || 0} sessions</p>
 <div className="mt-5 space-y-3">
 {items.length === 0 && <p className="text-sm text-muted_text">No data yet.</p>}
 {items.map((i) => {
 const pct = totalForStep ? Math.round((i.count / totalForStep) * 100) : 0;
 return (
 <div key={i.value}>
 <div className="flex items-center justify-between text-sm">
 <span className="text-ink">{labelMap[i.value] || i.value}</span>
 <span className="text-muted_text tabular-nums">{i.count} <span className="text-xs">· {pct}%</span></span>
 </div>
 <div className="mt-1.5 h-1.5 rounded-full bg-cream overflow-hidden">
 <div className="h-full bg-gold-500" style={{ width: `${pct}%` }} />
 </div>
 </div>
 );
 })}
 </div>
 </div>
 );
}

function CareMatchFunnel({ data, loading }) {
 if (loading || !data) {
 return <div className="card-soft p-12 text-center text-muted_text" data-testid="funnel-loading">Loading funnel...</div>;
 }
 const t = data.totals || {};
 const r = data.rates || {};
 const top = data.top_per_step || {};

 return (
 <div className="space-y-6">
 <div className="card-soft p-6 sm:p-7 flex items-start gap-4">
 <div className="w-11 h-11 rounded-2xl bg-gold-500/15 text-gold-600 flex items-center justify-center shrink-0">
 <Sparkles className="w-5 h-5" strokeWidth={1.75} />
 </div>
 <div>
 <p className="font-heading text-base font-semibold text-ink">Care Match funnel, anonymous</p>
 <p className="mt-1 text-sm text-muted_text leading-relaxed">No PII collected. Each visitor gets a per-session ID; we track which steps they reach, which options they pick, and whether they continue to booking and complete one.</p>
 </div>
 </div>

 <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
 <FunnelStep label="Started" value={t.starts ?? 0} helper="Quiz opened" tone="slate" />
 <FunnelStep label="Completed" value={t.completes ?? 0} helper={`${r.complete_rate ?? 0}% of starts`} tone="gold" />
 <FunnelStep label="Booking clicks" value={t.book_clicks ?? 0} helper={`${r.complete_to_click_rate ?? 0}% of completes`} tone="sage" />
 <FunnelStep label="Bookings" value={t.booked ?? 0} helper={`${r.click_to_book_rate ?? 0}% of clicks · ${r.overall_conversion ?? 0}% overall`} tone="ink" />
 </div>

 <div className="grid lg:grid-cols-2 gap-5">
 <StepBreakdown stepKey="concern" items={top.concern || []} totalSessions={data.step_completion?.concern} />
 <StepBreakdown stepKey="age" items={top.age || []} totalSessions={data.step_completion?.age} />
 <StepBreakdown stepKey="telehealth" items={top.telehealth || []} totalSessions={data.step_completion?.telehealth} />
 <StepBreakdown stepKey="urgency" items={top.urgency || []} totalSessions={data.step_completion?.urgency} />
 </div>

 <div className="card-soft p-6">
 <p className="font-heading text-base font-semibold text-ink">Suggested service mix</p>
 <p className="text-xs text-muted_text mt-1">From {t.completes ?? 0} completed quizzes</p>
 <div className="mt-5 space-y-3" data-testid="funnel-suggested">
 {(data.top_suggested || []).length === 0 && <p className="text-sm text-muted_text">No completions yet.</p>}
 {(data.top_suggested || []).map((s) => {
 const total = (data.top_suggested || []).reduce((a, x) => a + x.count, 0);
 const pct = total ? Math.round((s.count / total) * 100) : 0;
 return (
 <div key={s.service}>
 <div className="flex items-center justify-between text-sm">
 <span className="text-ink">{s.service}</span>
 <span className="text-muted_text tabular-nums">{s.count} · {pct}%</span>
 </div>
 <div className="mt-1.5 h-1.5 rounded-full bg-cream overflow-hidden">
 <div className="h-full bg-sage-700" style={{ width: `${pct}%` }} />
 </div>
 </div>
 );
 })}
 </div>
 </div>
 </div>
 );
}


function ConsentsTable({ rows, loading }) {
  const [countersignFor, setCountersignFor] = useState(null); // consent row being signed
  const [clinician, setClinician] = useState("");
  const [busy, setBusy] = useState(false);
  const [freshRows, setFreshRows] = useState(rows);
  const [requireCountersign, setRequireCountersign] = useState(null); // null=unknown, bool once loaded
  const [savingSetting, setSavingSetting] = useState(false);
  useEffect(() => { setFreshRows(rows); }, [rows]);

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/settings")
      .then((r) => { if (!cancelled) setRequireCountersign(!!r.data.require_countersign_before_confirm); })
      .catch(() => { if (!cancelled) setRequireCountersign(false); });
    return () => { cancelled = true; };
  }, []);

  async function toggleRequireCountersign(next) {
    setSavingSetting(true);
    try {
      const { data } = await api.patch("/admin/settings", { require_countersign_before_confirm: next });
      setRequireCountersign(!!data.require_countersign_before_confirm);
      toast.success(next ? "Countersign now required before confirming" : "Countersign requirement disabled");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save setting");
    } finally {
      setSavingSetting(false);
    }
  }

  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

  async function submitCountersign(e) {
    e.preventDefault();
    if (!countersignFor) return;
    if (!clinician.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/consents/${countersignFor.id}/countersign`, {
        clinician_name: clinician.trim(),
        typed_signature: clinician.trim(),
      });
      setFreshRows((prev) => prev.map((r) => (r.id === data.id ? data : r)));
      toast.success("Countersigned.");
      setCountersignFor(null);
      setClinician("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Countersign failed");
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="card-soft p-8 text-muted_text text-sm">Loading consents…</div>;

  return (
    <>
      <div
        data-testid="consent-policy-strip"
        className="card-soft p-5 mb-4 flex flex-wrap items-center justify-between gap-3"
      >
        <div className="min-w-0">
          <p className="font-heading text-base font-semibold text-ink inline-flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-sage-700" strokeWidth={1.75} /> Countersign-required policy
          </p>
          <p className="mt-1 text-xs text-muted_text max-w-2xl">
            When ON, no appointment can be marked <span className="font-medium text-ink">confirmed</span> until the clinician has countersigned the patient&apos;s consent. Patients still book normally; only the clinical confirm step is gated.
          </p>
        </div>
        <label className="inline-flex items-center gap-3 cursor-pointer select-none">
          <span className={`text-xs font-medium tracking-wider uppercase ${requireCountersign ? "text-sage-700" : "text-muted_text"}`}>
            {requireCountersign === null ? "Loading…" : requireCountersign ? "ON" : "OFF"}
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={!!requireCountersign}
            data-testid="require-countersign-toggle"
            onClick={() => !savingSetting && requireCountersign !== null && toggleRequireCountersign(!requireCountersign)}
            disabled={savingSetting || requireCountersign === null}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition disabled:opacity-60 ${requireCountersign ? "bg-sage-700" : "bg-[#d9e0d6]"}`}
          >
            <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition ${requireCountersign ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
        </label>
      </div>

      <div className="card-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table data-testid="consents-table" className="w-full text-sm">
            <thead className="bg-cream text-muted_text text-xs uppercase tracking-[0.16em]">
              <tr>
                <th className="text-left px-5 py-4 font-semibold">Patient</th>
                <th className="text-left px-5 py-4 font-semibold">Signed</th>
                <th className="text-left px-5 py-4 font-semibold">Signature</th>
                <th className="text-left px-5 py-4 font-semibold">Countersign</th>
                <th className="text-left px-5 py-4 font-semibold">Appointment</th>
                <th className="text-left px-5 py-4 font-semibold">IP</th>
                <th className="text-right px-5 py-4 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {freshRows.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-12 text-center text-muted_text" data-testid="consents-empty">No consents signed yet.</td></tr>
              )}
              {freshRows.map((c) => (
                <tr key={c.id} data-testid={`consent-row-${c.id}`} className="border-t border-[#E8EBE4] align-top">
                  <td className="px-5 py-4">
                    <p className="font-medium text-ink">{c.patient_name}</p>
                    <p className="text-xs text-muted_text">{c.patient_email}</p>
                  </td>
                  <td className="px-5 py-4">
                    <p className="text-ink">{format(new Date(c.signed_at), "EEE, MMM d yyyy")}</p>
                    <p className="text-xs text-muted_text">{format(new Date(c.signed_at), "h:mm a") + " UTC"}</p>
                  </td>
                  <td className="px-5 py-4 font-heading italic text-ink">{c.typed_signature}</td>
                  <td className="px-5 py-4">
                    {c.countersigned_at ? (
                      <div data-testid={`consent-countersigned-${c.id}`}>
                        <p className="font-heading italic text-ink text-sm">{c.clinician_signature}</p>
                        <p className="text-[11px] text-muted_text">{c.clinician_name}</p>
                        <p className="text-[11px] text-muted_text">{format(new Date(c.countersigned_at), "MMM d, h:mm a") + " UTC"}</p>
                      </div>
                    ) : (
                      <span data-testid={`consent-pending-${c.id}`} className="inline-flex items-center gap-1 text-xs px-2.5 h-6 rounded-full bg-amber-500/15 text-amber-700 whitespace-nowrap">
                        <Clock className="w-3.5 h-3.5" /> Awaiting review
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-xs text-muted_text font-mono">{c.appointment_id.slice(0, 8)}…</td>
                  <td className="px-5 py-4 text-xs text-muted_text">{c.ip_address || "—"}</td>
                  <td className="px-5 py-4 text-right">
                    <div className="inline-flex gap-2">
                      <a
                        data-testid={`consent-pdf-${c.id}`}
                        href={`${apiBase}/api/admin/consents/${c.id}/pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-ghost !text-xs !h-8 !px-3"
                        title="Download branded PDF"
                      >
                        <Download className="w-3.5 h-3.5 mr-1" /> PDF
                      </a>
                      {!c.countersigned_at && (
                        <button
                          data-testid={`consent-countersign-btn-${c.id}`}
                          onClick={() => { setCountersignFor(c); setClinician(""); }}
                          className="btn-gold !text-xs !h-8 !px-3"
                          title="Add clinician countersignature"
                        >
                          <PenTool className="w-3.5 h-3.5 mr-1" /> Countersign
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {countersignFor && (
        <div
          data-testid="countersign-dialog"
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink/50 px-4"
          onClick={() => !busy && setCountersignFor(null)}
        >
          <form
            onSubmit={submitCountersign}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4"
          >
            <div>
              <p className="text-xs uppercase tracking-[0.16em] text-gold-700 font-semibold">Clinician Countersignature</p>
              <h3 className="mt-1 font-heading text-xl font-semibold text-ink">Confirm review of consent</h3>
              <p className="mt-2 text-sm text-muted_text">
                Patient: <span className="font-medium text-ink">{countersignFor.patient_name}</span> · Signed {format(new Date(countersignFor.signed_at), "MMM d, yyyy")}
              </p>
            </div>
            <label className="block text-xs font-medium tracking-wider uppercase text-muted_text" htmlFor="clinician-name">
              Type your full name and credentials
            </label>
            <input
              id="clinician-name"
              data-testid="countersign-input"
              type="text"
              value={clinician}
              onChange={(e) => setClinician(e.target.value)}
              placeholder="Dr. Firstname Lastname, MD"
              autoComplete="off"
              className="w-full rounded-xl border border-[#E8EBE4] bg-white px-4 py-3 font-heading text-lg italic text-ink focus:outline-none focus:border-sage-600 focus:ring-2 focus:ring-sage-600/20"
              required
            />
            <p className="text-[11px] text-muted_text">Your typed name serves as your electronic signature. Timestamp will be captured automatically.</p>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" data-testid="countersign-cancel" onClick={() => setCountersignFor(null)} disabled={busy} className="btn-ghost !text-sm">Cancel</button>
              <button type="submit" data-testid="countersign-submit" disabled={busy || clinician.trim().length < 2} className="btn-gold !text-sm disabled:opacity-60">
                {busy ? "Signing…" : "Countersign"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}


